import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Home, Inbox, MapPin } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { api } from "~/lib/api";
import { profileComplete, useMe } from "~/lib/auth";
import type { IUser } from "~/lib/proto";
import { cn } from "~/lib/utils";

const METHODS = [
  { value: "locker", label: "ParcelPoint locker", hint: "Pick it up from a locker near you", icon: Inbox },
  { value: "home", label: "Home delivery", hint: "We bring it to your door", icon: Home },
  { value: "custom", label: "Somewhere else", hint: "An office, a shop, or another drop you choose", icon: MapPin },
];

export type DeliveryValue = { method: string; area: string; address: string };

export function deliveryError(value: DeliveryValue, me: IUser | null | undefined): string | null {
  if (!value.method) return "Choose how you'd like to receive it.";
  if (value.method === "locker" && !value.area.trim()) return "Choose a pickup location.";
  if (value.method === "home" && !profileComplete(me)) return "Save your home address to use home delivery.";
  if (value.method === "custom" && !value.address.trim()) return "Tell us where to deliver it.";
  return null;
}

export function DeliveryPicker({
  value,
  onChange,
  error,
}: {
  value: DeliveryValue;
  onChange: (value: DeliveryValue) => void;
  error?: string | null;
}) {
  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label="Delivery method" className="grid gap-2">
        {METHODS.map((option) => {
          const on = value.method === option.value;
          const Icon = option.icon;
          return (
            <div
              key={option.value}
              className={cn("bg-surface rounded-lg border transition-colors", on ? "border-foreground" : "border-hairline")}
            >
              <button
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChange({ ...value, method: option.value })}
                className="flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left"
              >
                <Icon className="size-5 shrink-0" strokeWidth={1.75} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium">{option.label}</span>
                  <span className="text-muted-foreground block text-[13px]">{option.hint}</span>
                </span>
                <span
                  aria-hidden
                  className={cn(
                    "grid size-5 shrink-0 place-items-center rounded-full border",
                    on ? "border-foreground" : "border-hairline",
                  )}
                >
                  {on && <span className="bg-foreground size-2.5 rounded-full" />}
                </span>
              </button>
              {on && (
                <div className="px-4 pb-4">
                  {option.value === "locker" && (
                    <LockerSelect area={value.area} onArea={(area) => onChange({ ...value, area })} />
                  )}
                  {option.value === "home" && <HomeAddress />}
                  {option.value === "custom" && (
                    <Input
                      density="comfortable"
                      aria-label="Where should we deliver it?"
                      placeholder="Building, street and area"
                      value={value.address}
                      onChange={(e) => onChange({ ...value, address: e.target.value })}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="text-destructive type-footnote">
          {error}
        </p>
      )}
    </div>
  );
}

function LockerSelect({ area, onArea }: { area: string; onArea: (area: string) => void }) {
  const locations = useQuery({
    queryKey: ["locker-locations"],
    queryFn: () => api.locations(),
    staleTime: 5 * 60 * 1000,
  });

  if (locations.isError) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-destructive type-footnote">We could not load the pickup locations.</p>
        <Button type="button" variant="outline" className="h-11 px-4 text-[14px]" onClick={() => void locations.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <Select value={area} onValueChange={onArea}>
      <SelectTrigger size="lg" className="w-full" aria-label="Pickup location">
        <SelectValue placeholder={locations.isLoading ? "Loading locations…" : "Choose a pickup location"} />
      </SelectTrigger>
      <SelectContent>
        {(locations.data ?? []).map((location) => (
          <SelectItem key={location.id ?? location.name} value={location.name ?? ""} className="min-h-11 text-[15px]">
            {location.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function HomeAddress() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const saved = profileComplete(me);
  const [editing, setEditing] = useState(false);
  const [area, setArea] = useState(me?.locationArea ?? "");
  const [town, setTown] = useState(me?.town ?? "");
  const [county, setCounty] = useState(me?.county ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (saved && !editing) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 text-[15px]">{[me?.locationArea, me?.town, me?.county].filter(Boolean).join(", ")}</p>
        <Button type="button" variant="outline" className="h-11 shrink-0 px-4 text-[14px]" onClick={() => setEditing(true)}>
          Change
        </Button>
      </div>
    );
  }

  async function save() {
    if (!area.trim() || !town.trim()) {
      setError("Enter your area or estate, and your town.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await api.updateProfile({ locationArea: area.trim(), town: town.trim(), county: county.trim() });
      qc.setQueryData(["me"], updated);
      setEditing(false);
    } catch {
      setError("We could not save your address. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <Input
        density="comfortable"
        aria-label="Area or estate"
        placeholder="Area or estate, for example Westlands"
        autoComplete="address-level3"
        value={area}
        onChange={(e) => setArea(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          density="comfortable"
          aria-label="Town"
          placeholder="Town"
          autoComplete="address-level2"
          value={town}
          onChange={(e) => setTown(e.target.value)}
        />
        <Input
          density="comfortable"
          aria-label="County, optional"
          placeholder="County (optional)"
          autoComplete="address-level1"
          value={county}
          onChange={(e) => setCounty(e.target.value)}
        />
      </div>
      {error && (
        <p role="alert" className="text-destructive type-footnote">
          {error}
        </p>
      )}
      <Button type="button" variant="secondary" className="h-11 w-full text-[15px]" loading={busy} onClick={() => void save()}>
        Save home address
      </Button>
    </div>
  );
}
